import styled, { DefaultTheme } from 'styled-components';
import { Card, Button, Table, Badge, Tabs, Modal, Alert } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { StyledComponentProps } from './types';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  padding: 16px;
  background: ${(props) => getThemeValue(props, '#141414', '#f5f5f5')};
  transition: background 0.3s ease;
`;

export const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

export const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const HeaderRight = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

export const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const Title = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
  transition: color 0.3s ease;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

export const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#8c8c8c', '#8c8c8c')};
  transition: color 0.3s ease;
`;

export const StyledCard = styled(Card)`
  background: ${(props) => getThemeValue(props, '#1f1f1f', '#ffffff')};
  border-color: ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  transition: all 0.3s ease;

  .ant-card-body {
    padding: 16px;

    @media (max-width: 768px) {
      padding: 12px;
    }
  }
`;

export const FiltersCard = styled(StyledCard)`
  .ant-card-body {
    padding: 16px;
  }
`;

export const FiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const FiltersRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  flex-wrap: wrap;
  align-items: flex-end;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const FilterItem = styled.div`
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 768px) {
    min-width: 100%;
  }

  > div:not(:first-child) {
    height: 40px;
    
    .ant-picker {
      height: 40px !important;
      min-height: 40px !important;
    }
    
    .ant-select-selector {
      height: 40px !important;
      min-height: 40px !important;
    }
    
    .ant-picker-input > input {
      height: 38px;
      line-height: 38px;
    }
  }
`;

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const StatisticDescription = styled.div`
  margin-top: 12px;
  font-size: 13px;
  font-weight: 400;
  color: ${(props) => getThemeValue(props, '#a0aec0', '#666666')};
  line-height: 1.5;
`;

export const SummaryCard = styled(StyledCard)<{ color?: string }>`
  border-radius: 16px;
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};
  background: ${(props) => {
    if (props.color) {
      return getThemeValue(
        props,
        props.color === 'blue' ? '#1a202c' : props.color === 'green' ? '#1a202c' : props.color === 'orange' ? '#1a202c' : '#1a202c',
        props.color === 'blue' ? '#f0f9ff' : props.color === 'green' ? '#f0fdf4' : props.color === 'orange' ? '#fff7ed' : '#faf5ff'
      );
    }
    return getThemeValue(props, '#1a202c', '#ffffff');
  }};
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)',
      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  margin-bottom: 0;

  &:hover {
    box-shadow: ${(props) =>
      getThemeValue(
        props,
        '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
        '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
      )};
  }

  .ant-card-body {
    padding: 20px;
    background: transparent;
    
    @media (min-width: 768px) {
      padding: 24px 28px;
    }
    
    @media (min-width: 1024px) {
      padding: 28px 32px;
    }
  }

  .ant-statistic {
    .ant-statistic-title {
      color: ${(props) => getThemeValue(props, '#a0aec0', '#666666')} !important;
      font-size: 14px;
      font-weight: 500;
      margin-bottom: 8px;
    }

    .ant-statistic-content {
      display: flex;
      align-items: baseline;

      .ant-statistic-content-value {
        font-size: 28px;
        font-weight: 700;
        line-height: 1.2;
      }

      .ant-statistic-content-prefix {
        margin-right: 4px;
        font-size: 20px;
      }
    }
  }
`;

export const BreakdownGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const BreakdownCard = styled(StyledCard)<{ variant: 'blue' | 'green' | 'orange' | 'purple' }>`
  background: ${(props) => {
    const colors = {
      blue: { light: '#e6f7ff', dark: '#001529' },
      green: { light: '#f6ffed', dark: '#162312' },
      orange: { light: '#fff7e6', dark: '#2b1d11' },
      purple: { light: '#f9f0ff', dark: '#1a1325' },
    };
    return getThemeValue(props, colors[props.variant].dark, colors[props.variant].light);
  }};
  border-color: ${(props) => {
    const colors = {
      blue: { light: '#91d5ff', dark: '#1890ff' },
      green: { light: '#b7eb8f', dark: '#52c41a' },
      orange: { light: '#ffd591', dark: '#fa8c16' },
      purple: { light: '#d3adf7', dark: '#722ed1' },
    };
    return getThemeValue(props, colors[props.variant].dark, colors[props.variant].light);
  }};
  border-width: 1px;
  border-style: solid;
`;

export const BreakdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

export const BreakdownTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const BreakdownTitleText = styled.span`
  font-weight: 600;
  font-size: 16px;
  color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
`;

export const BreakdownBadge = styled(Badge)`
  .ant-badge-status-dot {
    width: 8px;
    height: 8px;
  }
`;

export const BreakdownStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const BreakdownStatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const BreakdownStatLabel = styled.span`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#8c8c8c', '#8c8c8c')};
`;

export const BreakdownStatValue = styled.span<{ color?: string }>`
  font-weight: ${(props) => (props.color ? '600' : 'normal')};
  font-size: 14px;
  color: ${(props) => {
    if (props.color) {
      return props.color;
    }
    return getThemeValue(props, '#ffffff', '#262626');
  }};
`;

export const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${(props) => getThemeValue(props, '#2d3748', '#f5f5f5')};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${(props) => getThemeValue(props, '#718096', '#d1d5db')};
    border-radius: 3px;
    
    &:hover {
      background: ${(props) => getThemeValue(props, '#a0aec0', '#9ca3af')};
    }
  }
  
  @media (min-width: 768px) {
    overflow-x: visible;
  }
`;

export const StyledTable = styled(Table)`
  .ant-table {
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
    min-width: 600px;
  }

  .ant-table-thead > tr > th {
    background: ${(props) => getThemeValue(props, '#2d3748', '#fafafa')} !important;
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')} !important;
    font-weight: 600;
    font-size: 13px;
    padding: 12px 8px;
    white-space: nowrap;
    
    @media (min-width: 768px) {
      font-size: 14px;
      padding: 12px 16px;
    }
  }

  .ant-table-tbody > tr > td {
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')} !important;
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')} !important;
    font-size: 13px;
    padding: 12px 8px;
    white-space: nowrap;
    
    @media (min-width: 768px) {
      font-size: 14px;
      padding: 12px 16px;
    }
  }

  .ant-table-tbody > tr:hover > td {
    background: ${(props) => getThemeValue(props, '#2d3748', '#f5f5f5')} !important;
  }
`;

export const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 0;
    padding: 0;
    
    &::before {
      border-bottom: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')};
    }
  }

  .ant-tabs-tab {
    color: ${(props) => getThemeValue(props, '#8c8c8c', '#595959')};
    border: none;
    border-radius: 8px 8px 0 0;
    margin-right: 4px;
    margin-left: 0;
    padding: 12px 20px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    background: #ffffff !important;
    position: relative;
    
    .ant-tabs-tab-btn {
      font-size: 16px;
      line-height: 1.5;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: all 0.2s ease;
    }
    
    &:hover {
      color: #2D6CCA;
      background: rgba(45, 108, 202, 0.05);
    }
  }

  .ant-tabs-tab-active {
    background: #ffffff;
    border-bottom: 3px solid #2D6CCA;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
    
    .ant-tabs-tab-btn {
      color: #2D6CCA;
      font-size: 16px;
      font-weight: 500;
      line-height: 1.5;
    }
    
    &::before {
      content: '';
      position: absolute;
      bottom: -1px;
      left: 0;
      right: 0;
      height: 3px;
      background: #2D6CCA;
      border-radius: 3px 3px 0 0;
    }
  }

  .ant-tabs-ink-bar {
    display: none;
  }

  .ant-tabs-content-holder {
    padding-top: 24px;
    padding-bottom: 0;
  }

  .ant-tabs-content {
    padding: 0;
  }

  .ant-tabs-tabpane {
    padding: 0;
  }
`;

export const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: ${(props) => getThemeValue(props, '#1f1f1f', '#ffffff')};
  }

  .ant-modal-header {
    background: ${(props) => getThemeValue(props, '#1f1f1f', '#ffffff')};
    border-bottom-color: ${(props) => getThemeValue(props, '#434343', '#f0f0f0')};
  }

  .ant-modal-title {
    color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
  }

  .ant-modal-body {
    color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
  }

  .ant-modal-close {
    color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
  }
`;

export const StyledAlert = styled(Alert)`
  margin-top: 16px;
`;

export const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 24px;
  gap: 24px;
`;

export const ErrorIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => getThemeValue(props, '#2a1215', '#fff1f0')};
  color: ${(props) => getThemeValue(props, '#ff4d4f', '#ff4d4f')};
`;

export const NoDataContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 24px;
  gap: 24px;
`;

export const NoDataIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => getThemeValue(props, '#111b26', '#e6f7ff')};
  color: ${(props) => getThemeValue(props, '#1890ff', '#1890ff')};
`;

export const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  gap: 16px;
`;

export const LoadingText = styled.p`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#8c8c8c', '#8c8c8c')};
`;

export const CategoryCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const CategoryName = styled.span<{ isStripe?: boolean }>`
  text-transform: capitalize;
  font-weight: ${(props) => (props.isStripe ? '600' : 'normal')};
  color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
`;

export const StripeTooltip = styled.span`
  cursor: help;
  color: #ff4d4f;
`;

export const ErrorTitle = styled(Title)`
  margin-bottom: 8px;
`;

export const ErrorSubtitle = styled(Subtitle)`
  color: #ff4d4f;
  margin-top: 8px;
`;

export const NoDataTitle = styled(Title)`
  margin-bottom: 8px;
`;

export const NoDataReasonsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
`;

export const NoDataReasonItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const TabIcon = styled(FontAwesomeIcon)`
  margin-right: 8px;
`;

export const FilterLabel = styled.div<StyledComponentProps>`
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
`;

export const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ModalStatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

export const IconWithColor = styled(FontAwesomeIcon)<{ iconcolor?: string }>`
  color: ${(props) => props.iconcolor || 'inherit'};
  font-size: 20px;
`;

export const AmountCell = styled.span<{ isstripe?: string }>`
  color: ${(props) => (props.isstripe === 'true' ? '#ff4d4f' : 'inherit')};
  font-weight: ${(props) => (props.isstripe === 'true' ? '600' : 'normal')};
`;

export const ActionButton = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #2D6CCA;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  
  svg {
    font-size: 14px;
  }
`;

export const StyledButton = styled(Button)`
  background-color: #2D6CCA !important;
  color: white !important;
  border-radius: 8px !important;
  font-size: 16px !important;
  border: none !important;
  
  &:hover {
    background-color: #2560b8 !important;
    color: white !important;
  }
  
  &.ant-btn-link {
    background-color: transparent !important;
    color: #2D6CCA !important;
    border-radius: 8px !important;
    font-size: 16px !important;
    
    &:hover {
      color: #2560b8 !important;
      background-color: transparent !important;
    }
  }
  
  &.ant-btn-text {
    background-color: #2D6CCA !important;
    color: white !important;
    border-radius: 8px !important;
    font-size: 16px !important;
    border: none !important;
    
    &:hover {
      background-color: #2560b8 !important;
      color: white !important;
    }
  }
  
  &.ant-btn-default {
    background-color: #2D6CCA !important;
    color: white !important;
    border-radius: 8px !important;
    font-size: 16px !important;
    border: none !important;
    
    &:hover {
      background-color: #2560b8 !important;
      color: white !important;
      border: none !important;
    }
  }
`;
